import TooltipIcon from "@icons/question.svg?react";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./tooltip.module.scss";

interface Props {
	title: string;
	position?: "top" | "bottom" | "left" | "right";
	className?: string;
	onDocsClick?: () => void;
	docsLabel?: string;
}

const OFFSET = 12;
const VIEWPORT_PADDING = 8;
const HIDE_DELAY = 150;

const Tooltip = ({
	title,
	position = "top",
	className = "",
	onDocsClick,
	docsLabel,
}: Props) => {
	const tooltipRef = useRef<HTMLDivElement>(null);
	const [style, setStyle] = useState<React.CSSProperties>({});
	const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
	const [open, setOpen] = useState(false);
	const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const interactive = Boolean(onDocsClick);

	const calculate = useCallback(
		(el: Element) => {
			const tip = tooltipRef.current;
			if (!tip) return;

			const iconRect = el.getBoundingClientRect();
			const tipRect = tip.getBoundingClientRect();
			const vw = window.innerWidth;
			const vh = window.innerHeight;

			const iconCenterX = iconRect.left + iconRect.width / 2;
			const iconCenterY = iconRect.top + iconRect.height / 2;

			let pos = position;

			if (pos === "bottom" && iconRect.bottom + OFFSET + tipRect.height > vh) {
				pos = "top";
			} else if (pos === "top" && iconRect.top - OFFSET - tipRect.height < 0) {
				pos = "bottom";
			} else if (
				pos === "right" &&
				iconRect.right + OFFSET + tipRect.width > vw
			) {
				pos = "left";
			} else if (pos === "left" && iconRect.left - OFFSET - tipRect.width < 0) {
				pos = "right";
			}

			const newStyle: React.CSSProperties = {};
			const newArrow: React.CSSProperties = {};

			if (pos === "bottom" || pos === "top") {
				let left = iconCenterX - tipRect.width / 2;
				if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
				if (left + tipRect.width > vw - VIEWPORT_PADDING) {
					left = vw - VIEWPORT_PADDING - tipRect.width;
				}

				newStyle.left = `${left}px`;
				newStyle.right = "auto";

				const arrowLeft = iconCenterX - left;
				newArrow.left = `${Math.max(12, Math.min(arrowLeft, tipRect.width - 12))}px`;
				newArrow.transform = "translateX(-50%)";

				if (pos === "bottom") {
					newStyle.top = `${iconRect.bottom + OFFSET}px`;
					newArrow.bottom = "100%";
					newArrow.borderBottom = "8px solid #141414";
					newArrow.borderTop = "8px solid transparent";
				} else {
					newStyle.top = `${iconRect.top - OFFSET - tipRect.height}px`;
					newArrow.top = "100%";
					newArrow.borderTop = "8px solid #141414";
					newArrow.borderBottom = "8px solid transparent";
				}
				newArrow.borderLeft = "8px solid transparent";
				newArrow.borderRight = "8px solid transparent";
			} else {
				let top = iconCenterY - tipRect.height / 2;
				if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
				if (top + tipRect.height > vh - VIEWPORT_PADDING) {
					top = vh - VIEWPORT_PADDING - tipRect.height;
				}

				newStyle.top = `${top}px`;

				const arrowTop = iconCenterY - top;
				newArrow.top = `${Math.max(12, Math.min(arrowTop, tipRect.height - 12))}px`;
				newArrow.transform = "translateY(-50%)";

				if (pos === "right") {
					newStyle.left = `${iconRect.right + OFFSET}px`;
					newArrow.right = "100%";
					newArrow.borderRight = "8px solid #141414";
					newArrow.borderLeft = "8px solid transparent";
				} else {
					newStyle.left = `${iconRect.left - OFFSET - tipRect.width}px`;
					newArrow.left = "100%";
					newArrow.borderLeft = "8px solid #141414";
					newArrow.borderRight = "8px solid transparent";
				}
				newArrow.borderTop = "8px solid transparent";
				newArrow.borderBottom = "8px solid transparent";
			}

			setStyle(newStyle);
			setArrowStyle(newArrow);
		},
		[position],
	);

	const scheduleHide = useCallback(() => {
		hideTimer.current = setTimeout(() => setOpen(false), HIDE_DELAY);
	}, []);

	const cancelHide = useCallback(() => {
		if (hideTimer.current) {
			clearTimeout(hideTimer.current);
			hideTimer.current = null;
		}
	}, []);

	const tooltipClasses = interactive
		? `${styles.tooltip} ${styles.interactive} ${open ? styles.visible : ""}`
		: `${styles.tooltip} ${open ? styles.visible : ""}`;

	const tooltipContent = createPortal(
		<div
			ref={tooltipRef}
			role="tooltip"
			id="help-tooltip"
			className={tooltipClasses}
			style={style}
			onMouseEnter={cancelHide}
			onMouseLeave={scheduleHide}
		>
			{interactive ? (
				<>
					<span>{title}</span>
					{onDocsClick && (
						<button
							type="button"
							className={styles.docs_link}
							onClick={(e) => {
								e.stopPropagation();
								setOpen(false);
								onDocsClick();
							}}
							data-testid="tooltip-docs-link-btn"
						>
							{docsLabel ?? "Learn more →"}
						</button>
					)}
				</>
			) : (
				title
			)}
			<span className={styles.arrow} style={arrowStyle} />
		</div>,
		document.body,
	);

	return (
		<div className={`${styles.tooltip_container} ${className}`}>
			<div>
				<div className={styles.tooltip_icon_wrapper}>
					<TooltipIcon
						aria-describedby="help-tooltip"
						className={styles.help_button}
						onMouseEnter={(e) => {
							cancelHide();
							calculate(e.currentTarget);
							setOpen(true);
						}}
						onMouseLeave={scheduleHide}
						data-testid="tooltip-trigger-icon"
					/>
					{tooltipContent}
				</div>
			</div>
		</div>
	);
};

export default Tooltip;
